import { withAsyncContext as _withAsyncContext, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "w-full flex flex-row reporting-widgets"
}
const _hoisted_2 = {
  key: 1,
  class: "w-full flex"
}
const _hoisted_3 = {
  key: 2,
  class: "dashboard-calendar bg-white border-round w-full mr-4 mb-4 overflow-hidden"
}
const _hoisted_4 = { class: "dashboard-calendar-row flex w-full h-auto" }
const _hoisted_5 = { class: "flex-1 border-100 border-left-2 md:min-width-full h-auto" }
const _hoisted_6 = {
  key: 1,
  class: "p-4 pb-2 z-1 relative"
}
const _hoisted_7 = { class: "font-bold" }
const _hoisted_8 = { class: "flex grid w-full text-base md:text-sm lg:text-base" }
const _hoisted_9 = { class: "col-6 md:col-5 lg:col-6" }
const _hoisted_10 = {
  key: 0,
  class: "col-5 flex p-1 text-base md:text-sm lg:text-base"
}
const _hoisted_11 = {
  key: 1,
  class: "col-1 flex pr-2 align-items-center justify-items-end",
  style: {"min-width":"16px"}
}
const _hoisted_12 = ["onClick"]
const _hoisted_13 = {
  key: 0,
  class: "dashboard-calendar-row flex p-3 pb-3 border-100 border-top-2 align-items-center"
}
const _hoisted_14 = { class: "w-full p-input-icon-left" }
const _hoisted_15 = { class: "flex align-items-center" }
const _hoisted_16 = { class: "text-sm" }
const _hoisted_17 = { class: "text-sm" }

import { onMounted, Ref, ref, watch, computed, onBeforeUnmount } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import { CalendarOptions, EventClickArg } from '@fullcalendar/core';
import {
  useAuthStore,
  useCalendarStore,
  useEventsModalsStore,
  useOrganizationStore,
  useUtilizationStore,
} from '@/store';
import listPlugin from '@fullcalendar/list';
import AutoComplete from 'primevue/autocomplete';
import Calendar from 'primevue/calendar';
import {
  can,
  useGetInitialDropdownOptions,
  usePersonTypes,
  modifiedRRulePlugin,
  useDateTime,
  useRequestCancel,
  useTenant,
} from '@/composables';
import { AttendeeDropdownOptionInterface, FiltersTherapyEventsInterface } from '@/types';
import SupportWidget from '@/components/SupportWidget/SupportWidget.vue';
import Evaluation from '@/components/Evaluation/Evaluation.vue';
import DashboardCaseloadAdditions from './components/DashboardCaseloadAdditions.vue';
import Attendance from '@/components/Attendance/Attendance.vue';
import SOWReporting from '@/components/Utilization/SOWReporting.vue';
import TimeRatioReporting from '@/components/Utilization/TimeRatioReporting.vue';
import EventsModals from '@/components/EventsModals';
import { PERMISSIONS, EVENT_TYPE, TENANT_SETTINGS } from '@/enum';
import Provider from '@/components/Provider';
import { Overage } from '..';


export default /*@__PURE__*/_defineComponent({
  __name: 'OverviewContent',
  async setup(__props) {

let __temp: any, __restore: any

const $tenant = useTenant();
const authStore = useAuthStore();
const calendarStore = useCalendarStore();
const eventsModalsStore = useEventsModalsStore();
const organizationStore = useOrganizationStore();
const utilizationStore = useUtilizationStore();
const requestCancel = useRequestCancel();
const today = new Date();
const calendar = ref();
const isClinician = ref(usePersonTypes.isClinician() || usePersonTypes.isCaseManager());
const isCaseManager = usePersonTypes.isCaseManager();
const isSupportAdminOrSchoolAdmin = usePersonTypes.isSupportAdminOrSchoolAdmin();
const isSuperAdmin = usePersonTypes.isSuperAdmin();
const canViewUtilizationWidgets =
  can([PERMISSIONS.CLINICIAN_ANALYTICS]) &&
  (isSuperAdmin || usePersonTypes.isSupportAdmin() || usePersonTypes.isBillingAdmin());
const selectedAttendees = ref<AttendeeDropdownOptionInterface[]>([]);
const firstDay = ref<string>(new Date(today.getFullYear(), today.getMonth(), 1).toLocaleDateString('fr-CA'));
const lastDay = ref<string>(new Date(today.getFullYear(), today.getMonth() + 1, 0).toLocaleDateString('fr-CA'));
const availableAttendees = ref([] as AttendeeDropdownOptionInterface[]);
availableAttendees.value =
  isClinician.value && can([PERMISSIONS.CLINICIAN_STUDENTS_INDEX])
    ? (
  ([__temp,__restore] = _withAsyncContext(() => useGetInitialDropdownOptions(authStore.user?.id as number).then((result) => {
        return result.availableAttendees.value;
      }))),
  __temp = await __temp,
  __restore(),
  __temp
)
    : [];

const allAttendees = [...availableAttendees.value];

const filteredStudents = ref();

const autocomplete = ref(false);

const organizationAssociated = computed(() => authStore.organizationAssociated.map((organization) => organization.key));

const canAccessClassroom = computed(() => {
  return isClinician.value || isParentOrFacilitatorOrStudent.value || usePersonTypes.isSchoolAdmin();
});

const isParentOrFacilitatorOrStudent = computed(() => {
  return usePersonTypes.isFacilitator() || usePersonTypes.isParent() || usePersonTypes.isStudent();
});

onMounted(async () => {
  if ($tenant.tenantSettingEnabled(TENANT_SETTINGS.UTILIZATION_TRACKING) && canViewUtilizationWidgets) {
    await utilizationStore.getUtilizationDashboardWidgetData();
  }

  if (can([PERMISSIONS.THERAPY_EVENT_INDEX])) {
    const filtersEvents: FiltersTherapyEventsInterface = can([PERMISSIONS.THERAPY_EVENT_INDEX_ALL])
      ? {
          start_at: firstDay.value,
          end_at: lastDay.value,
        }
      : {
          organizations: organizationAssociated.value,
          start_at: firstDay.value,
          end_at: lastDay.value,
        };

    if (organizationStore.organizationsIdsSelected.length > 0) {
      await getEventsBySelectedOrganizations();
    } else {
      await calendarStore.getEvents(filtersEvents);
    }

    await updateSelectedDate();
    filterIsGroupOrIndividualAttendee();
  }
});

onBeforeUnmount(() => {
  requestCancel.requestSaverAbortAll();
});

const isGroupEligibleAttendees = ref<AttendeeDropdownOptionInterface[]>([]);

const filterSelected = () => {
  if (!selectedAttendees.value.length) {
    availableAttendees.value = allAttendees;
    autocomplete.value = false;
    return;
  }

  autocomplete.value = true;

  if (isGroupEligibleAttendees.value.includes(selectedAttendees.value[0])) {
    availableAttendees.value = isGroupEligibleAttendees.value;
    return;
  }

  availableAttendees.value = selectedAttendees.value;
};

const isAlreadySelected = (data: { value: number }) => {
  return !!selectedAttendees.value.find((st) => st.value === data.value);
};

const selectedDate: Ref<Date> = ref(today);

const eventsOnSelectedDate = ref(false);

watch([() => calendarStore.loading, () => selectedDate.value], () => {
  eventsOnSelectedDate.value = document.getElementsByClassName('fc-list-event').length > 0;
});

const updateSelectedDate = async () => {
  await calendar.value?.getApi().gotoDate(selectedDate.value);

  if (!isDayInActualMonth(selectedDate.value)) {
    firstDay.value = new Date(selectedDate.value.getFullYear(), selectedDate.value.getMonth(), 1).toLocaleDateString(
      'fr-CA',
    );
    lastDay.value = new Date(selectedDate.value.getFullYear(), selectedDate.value.getMonth() + 1, 0).toLocaleDateString(
      'fr-CA',
    );
    if (organizationStore.organizationsIdsSelected.length > 0) {
      await calendarStore.getEventsByOrganization(
        firstDay.value,
        lastDay.value,
        organizationStore.organizationsIdsSelected,
      );
    } else {
      await calendarStore.getEvents({ start_at: firstDay.value, end_at: lastDay.value });
    }
  }

  calendarOptions.value.events = calendarStore.events;
};

const getEventsBySelectedOrganizations = async () => {
  await calendarStore
    .getEventsByOrganization(firstDay.value, lastDay.value, organizationStore.organizationsIdsSelected)
    .then(() => {
      calendarOptions.value.events = calendarStore.events;
    });
};

watch(() => selectedDate.value, updateSelectedDate);

watch(
  () => eventsModalsStore.isAttendeesSelectedFromDashboard,
  () => {
    if (eventsModalsStore.isAttendeesSelectedFromDashboard) {
      selectedAttendees.value = [];
      autocomplete.value = false;
      availableAttendees.value = allAttendees;
    }
  },
);

watch(
  () => organizationStore.organizationsIdsSelected,
  async () => {
    await getEventsBySelectedOrganizations();
  },
);

watch(
  () => calendarStore.events,
  () => (calendarOptions.value.events = calendarStore.rawEvents),
);

watch(
  () => authStore.personType?.acronym,
  async () => {
    isClinician.value = usePersonTypes.isClinician() || usePersonTypes.isCaseManager();
  },
);

watch(
  () => authStore.impersonateToken,
  async (value, oldValue) => {
    if (oldValue) {
      await getEventsBySelectedOrganizations();
    }
  },
);

watch(
  () => calendarStore.rawEvents,
  () => {
    calendarOptions.value.events = calendarStore.rawEvents;
  },
);

const isDayInActualMonth = (date: Date) => {
  let strDate = date.toLocaleDateString('fr-CA');
  return strDate >= firstDay.value && strDate <= lastDay.value;
};

const calendarOptions = ref<CalendarOptions>({
  plugins: [listPlugin, modifiedRRulePlugin],
  initialView: 'listDay',
  headerToolbar: false,
  listDayFormat: { month: 'short', day: 'numeric', weekday: 'short' },
  stickyHeaderDates: false,
  listDaySideFormat: false,
  height: '100%',
  eventBackgroundColor: '#ffffff',
  eventBorderColor: '#f1f3f3',
  eventTextColor: '#3B4242',
  eventMinHeight: 24,
  events: [],
  eventClassNames: (arg) => calendarStore.defineEventsClassNames(arg),
  eventClick: function (info: EventClickArg) {
    const isDocumented = info.event._def.extendedProps.eventStatus === 'documented';
    const isInPast = (info.event.start as Date) < new Date();
    const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);
    const showDocumentEvent = isDocumented || isInPast;
    const isCaseloadAndInCurrentMonth =
      isCaseloadManagement && useDateTime().isSameMonthAndYear(info.event.start as Date);

    if (showDocumentEvent && !isCaseloadAndInCurrentMonth) {
      onDocumentEvent(info);
    } else {
      onEditEvent(info);
    }
  },
  eventDidMount: (info) => {
    const eventTimeTd = info.el.getElementsByClassName('fc-list-event-time')[0];
    const start = useDateTime().formatTimeByTimezone(
      info.event.start as Date,
      info.event.extendedProps.hostTimezoneName,
    );
    const end = useDateTime().formatTimeByTimezone(info.event.end as Date, info.event.extendedProps.hostTimezoneName);

    if (eventTimeTd) {
      //need to catch it on the next cycle because it still loads the orig text
      setTimeout(() => {
        eventTimeTd.innerHTML = `${start} - ${end}  ${info.event.extendedProps.hostTimezone}`;
      }, 0);
    }
  },
});

const onDocumentEvent = async (info: EventClickArg) => {
  eventsModalsStore.onDocumentEvent(info.event);
};

const onEditEvent = async (info: EventClickArg) => {
  const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);

  if (isSupportAdminOrSchoolAdmin || isSuperAdmin || isCaseManager) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  if (isCaseloadManagement) {
    eventsModalsStore.onEditEvent(info.event);
    return;
  }

  if (isSupportAdminOrSchoolAdmin.value) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  if (info.event.classNames.includes('documented')) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  eventsModalsStore.onEditEvent(info.event);
};

const scheduleEvaluationEvent = (studentId?: number, dueAt?: Date) => {
  eventsModalsStore.preSelectedDueAt = dueAt as Date;
  const student = availableAttendees.value.find((attendee) => attendee.value == (studentId as number));
  if (student) eventsModalsStore.setAttendees([student]);
  eventsModalsStore.createNewEvent(EVENT_TYPE.EVALUATION);
};

const filterIsGroupOrIndividualAttendee = () => {
  availableAttendees.value.forEach((attendee) => {
    attendee.disciplines?.forEach((discipline) => {
      if (discipline.is_group) {
        const alreadyInArray = isGroupEligibleAttendees.value.findIndex((eligibleAttendee) => {
          return attendee.value === eligibleAttendee.value;
        });
        if (alreadyInArray === -1) {
          isGroupEligibleAttendees.value.push(attendee);
        }
      }
    });
  });
};

const searchStudent = (event: { query: string }) => {
  autocomplete.value = selectedAttendees.value.length > 0 || event.query.length >= 1;

  if (!event.query.trim()) {
    filteredStudents.value = [...availableAttendees.value];
    return;
  }

  filteredStudents.value = availableAttendees.value.filter((student) => {
    return student.name.toLowerCase().includes(event.query.toLowerCase());
  });
};

const redirecToClassroom = (classroomLink) => {
  window.open(classroomLink, '_blank');
};

return (_ctx: any,_cache: any) => {
  const _component_Loader = _resolveComponent("Loader")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputIcon = _resolveComponent("InputIcon")!
  const _component_IconField = _resolveComponent("IconField")!
  const _component_AppButton = _resolveComponent("AppButton")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref($tenant).tenantSettingEnabled(_unref(TENANT_SETTINGS).UTILIZATION_TRACKING) && _unref(canViewUtilizationWidgets))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (_unref(utilizationStore).loading)
            ? (_openBlock(), _createBlock(_component_Loader, {
                key: 0,
                class: "loader loader-height"
              }))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createVNode(SOWReporting, {
                  class: "sow-reporting-widget",
                  "sow-data": _unref(utilizationStore).sOWTableData
                }, null, 8, ["sow-data"]),
                _createVNode(TimeRatioReporting, {
                  class: "time-ratio-reporting-widget flex-auto",
                  "time-ratio-data": _unref(utilizationStore).timeRatioTableData
                }, null, 8, ["time-ratio-data"])
              ], 64))
        ]))
      : _createCommentVNode("", true),
    (_unref(can)([_unref(PERMISSIONS).OVERAGES_ALL]))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(Overage))
        ]))
      : _createCommentVNode("", true),
    (_unref(can)([_unref(PERMISSIONS).DASHBOARD_OVERVIEW_CALENDAR_VIEW_SCHEDULE_VIEW]))
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(Calendar), {
              modelValue: selectedDate.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedDate).value = $event)),
              inline: true
            }, null, 8, ["modelValue"]),
            _createElementVNode("div", _hoisted_5, [
              (_unref(calendarStore).loading)
                ? (_openBlock(), _createBlock(_component_Loader, {
                    key: 0,
                    class: "loader"
                  }))
                : _createCommentVNode("", true),
              (!_unref(calendarStore).loading && !eventsOnSelectedDate.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createElementVNode("h3", _hoisted_7, _toDisplayString(selectedDate.value.toLocaleDateString('en-US', { dateStyle: 'medium' })), 1),
                    _cache[5] || (_cache[5] = _createElementVNode("p", { class: "mt-2" }, "No events", -1))
                  ]))
                : _createCommentVNode("", true),
              _createVNode(_unref(FullCalendar), {
                ref_key: "calendar",
                ref: calendar,
                options: calendarOptions.value,
                class: "flex-1 w-full border-100 border-left-2 md:min-width-full",
                style: {"min-width":"360px","min-height":"240px"}
              }, {
                eventContent: _withCtx((arg) => [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, _toDisplayString(arg.event.title), 1),
                    (!isClinician.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                          _createVNode(_unref(Provider), {
                            "discipline-code": arg.event.extendedProps.discipline_code,
                            "service-provider-name": arg.event.extendedProps.classroom.service_provider_name
                          }, null, 8, ["discipline-code", "service-provider-name"])
                        ]))
                      : _createCommentVNode("", true),
                    (canAccessClassroom.value && arg.event.extendedProps.classroom.classroom_link)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                          _createElementVNode("a", {
                            onClick: _withModifiers(($event: any) => (redirecToClassroom(arg.event.extendedProps.classroom.classroom_link)), ["stop"])
                          }, [...(_cache[6] || (_cache[6] = [
                            _createElementVNode("span", { class: "material-icons text-primary" }, "videocam", -1)
                          ]))], 8, _hoisted_12)
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _: 1
              }, 8, ["options"])
            ])
          ]),
          (_unref(can)([_unref(PERMISSIONS).DASHBOARD_OVERVIEW_MEET_WITH_SEARCH_STUDENTS_VIEW]) && availableAttendees.value.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _createElementVNode("div", _hoisted_14, [
                  _createVNode(_component_IconField, { "icon-position": "left" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_InputIcon, {
                        class: "p-input-icon-left",
                        style: {"z-index":"100"}
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_Icon, {
                            name: "search",
                            class: "text-lg"
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(AutoComplete), {
                        modelValue: selectedAttendees.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedAttendees).value = $event)),
                        class: "w-full",
                        suggestions: filteredStudents.value,
                        multiple: true,
                        "option-label": "name",
                        "option-disabled": isAlreadySelected,
                        placeholder: "Create new event with...",
                        "remove-token-icon": "m-icon close text-sm",
                        onItemUnselect: filterSelected,
                        onItemSelect: filterSelected,
                        onComplete: _cache[2] || (_cache[2] = ($event: any) => (searchStudent($event))),
                        onClear: _cache[3] || (_cache[3] = ($event: any) => (autocomplete.value = false))
                      }, {
                        chip: _withCtx((slotProps) => [
                          _createElementVNode("div", _hoisted_15, [
                            _createElementVNode("span", _hoisted_16, _toDisplayString(slotProps.value.name), 1),
                            (slotProps.value.disciplines.some((group) => group.is_group))
                              ? (_openBlock(), _createBlock(_component_Icon, {
                                  key: 0,
                                  name: "group",
                                  class: "icon-group ml-1 text-sm text-color-secondary"
                                }))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        option: _withCtx((slotProps) => [
                          _createElementVNode("div", {
                            class: _normalizeClass(["flex align-items-center", { selected: isAlreadySelected(slotProps.option) }])
                          }, [
                            _createElementVNode("span", _hoisted_17, _toDisplayString(slotProps.option.name), 1),
                            (slotProps.option.disciplines.some((group) => group.is_group))
                              ? (_openBlock(), _createBlock(_component_Icon, {
                                  key: 0,
                                  name: "group",
                                  class: "icon-group ml-1 text-sm text-color-secondary"
                                }))
                              : _createCommentVNode("", true)
                          ], 2)
                        ]),
                        _: 1
                      }, 8, ["modelValue", "suggestions"])
                    ]),
                    _: 1
                  })
                ]),
                _createVNode(_component_AppButton, {
                  class: "ml-3",
                  type: "primary",
                  text: 
          selectedAttendees.value.length
            ? 'New Event with ' + selectedAttendees.value.length + ' attendee' + (selectedAttendees.value.length > 1 ? 's' : '')
            : 'New Event'
        ,
                  "icon-left": "add",
                  disabled: !_unref(can)([_unref(PERMISSIONS).DASHBOARD_OVERVIEW_MEET_WITH_SEARCH_STUDENTS_ADD_EVENT]),
                  onClick: _cache[4] || (_cache[4] = 
          () => _unref(eventsModalsStore).setAttendees(selectedAttendees.value, selectedAttendees.value.value ?? true).createNewEvent()
        )
                }, null, 8, ["text", "disabled"])
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (_unref(can)([_unref(PERMISSIONS).REFERRAL_CASELOAD]) && _unref($tenant).tenantSettingEnabled(_unref(TENANT_SETTINGS).REFERRALS))
      ? (_openBlock(), _createBlock(DashboardCaseloadAdditions, {
          key: 3,
          "organizations-ids": organizationAssociated.value,
          onScheduleEvent: scheduleEvaluationEvent
        }, null, 8, ["organizations-ids"]))
      : _createCommentVNode("", true),
    (_unref(can)([_unref(PERMISSIONS).WIDGET_ATTENDANCE_ALL]))
      ? (_openBlock(), _createBlock(Attendance, {
          key: 4,
          "organizations-ids": organizationAssociated.value
        }, null, 8, ["organizations-ids"]))
      : _createCommentVNode("", true),
    _createVNode(SupportWidget),
    (_unref(can)([_unref(PERMISSIONS).WIDGET_EVALUATION]))
      ? (_openBlock(), _createBlock(Evaluation, {
          key: 5,
          "organizations-ids": organizationAssociated.value,
          onScheduleEvent: scheduleEvaluationEvent
        }, null, 8, ["organizations-ids"]))
      : _createCommentVNode("", true),
    _createVNode(_unref(EventsModals))
  ], 64))
}
}

})